import { IFind } from '../create-report/interfaces';

const fieldOf = (condition: IFind): string => Object.keys(condition)[0]?.toLowerCase();

export function mergeFilters({ serverFiltersString, userFiltersString }: {
  serverFiltersString: string;
  userFiltersString: string;
}): string {
  const serverFiltersObject = serverFiltersString?.length ? JSON.parse(serverFiltersString) : null;
  const userFiltersObject = userFiltersString?.length ? JSON.parse(userFiltersString) : null;

  let serverFilters: IFind[];
  if (serverFiltersObject?.$and) {
    serverFilters = serverFiltersObject.$and;
  } else if (serverFiltersObject) {
    serverFilters = [serverFiltersObject];
  } else {
    serverFilters = [];
  }

  let userFilters: IFind[];
  if (userFiltersObject?.$and) {
    userFilters = userFiltersObject.$and;
  } else if (userFiltersObject) {
    userFilters = [userFiltersObject];
  } else {
    userFilters = [];
  }

  // A user filter replaces the report's own conditions on the same field: keeping both
  // would AND two values of one field together and match nothing.
  const overriddenFields = new Set(userFilters.map(fieldOf));
  const mergedConditions = serverFilters
    .filter((condition) => !overriddenFields.has(fieldOf(condition)))
    .concat(userFilters);

  if (mergedConditions.length === 0) {
    return '';
  } if (mergedConditions.length === 1) {
    return JSON.stringify(mergedConditions[0]);
  }
  return JSON.stringify({ $and: mergedConditions });
}

export function mergeQueryParamFilters(
  persistedFindString: string,
  queryFindString: string,
): string {
  if (!queryFindString?.length) {
    return persistedFindString || '';
  }

  const toConditions = (findString: string): IFind[] => {
    if (!findString?.length) return [];
    const parsed = JSON.parse(findString);
    if (parsed?.$and) return parsed.$and as IFind[];
    return Object.keys(parsed).map((key) => ({ [key]: parsed[key] }) as IFind);
  };

  const queryConditions = toConditions(queryFindString);
  const overriddenFields = new Set(queryConditions.map(fieldOf));

  const preservedConditions = toConditions(persistedFindString)
    .filter((condition) => !overriddenFields.has(fieldOf(condition)));

  const mergedConditions = preservedConditions.concat(queryConditions);

  if (mergedConditions.length === 0) {
    return '';
  } if (mergedConditions.length === 1) {
    return JSON.stringify(mergedConditions[0]);
  }
  return JSON.stringify({ $and: mergedConditions });
}

/**
 * Next query string for a report URL after the given filter was submitted.
 *
 * Only params the URL already carries are maintained — a deep-link param whose filter was
 * removed is dropped, one whose value changed is updated. Filters the user added by hand are
 * never published as params: they would be read back as the report's own filters by
 * `mergeQueryParamFilters`, and then rendered a second time next to the user's own row.
 */
export function syncedQueryParams(currentSearch: string, findString: string): string {
  const current = new URLSearchParams(currentSearch);
  const existingKeys = Array.from(new Set(Array.from(current.keys())));
  if (existingKeys.length === 0) return '';

  const equalityValues = new Map<string, string>();
  if (findString) {
    try {
      const collect = (node: unknown): void => {
        if (!node || typeof node !== 'object') return;
        Object.entries(node as Record<string, unknown>).forEach(([key, value]) => {
          if (key === '$and' && Array.isArray(value)) {
            value.forEach(collect);
            return;
          }
          if (value && typeof value === 'object' && '$eq' in (value as Record<string, unknown>)) {
            const eq = (value as { $eq: unknown }).$eq;
            if (eq !== undefined && eq !== null) {
              equalityValues.set(key.toLowerCase(), String(eq).replace(/"/g, ''));
            }
          }
        });
      };
      collect(JSON.parse(findString));
    } catch {
      // Leave the URL untouched when the filter is empty or malformed.
      return current.toString();
    }
  }

  const next = new URLSearchParams();
  existingKeys.forEach((key) => {
    const value = equalityValues.get(key.toLowerCase());
    if (value !== undefined) next.set(key, value);
  });
  return next.toString();
}

export function mergeSorts({ serverSortsString, userSortsString }: {
  serverSortsString: string;
  userSortsString: string;
}): string {
  const serverSortsObject = serverSortsString?.length ? JSON.parse(serverSortsString) : {};
  const userSortsObject = userSortsString?.length ? JSON.parse(userSortsString) : {};

  const mergedSortObject = {
    ...serverSortsObject,
    ...userSortsObject,
  };

  return JSON.stringify(mergedSortObject);
}
